import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { buildRetrocessionRowAmounts } from '../utils/retrocessions';
import { DateRange, countRangeMonths, dateRangesFilter, isDateInRanges } from '../utils/period';

interface Negotiator {
  id: string;
  name: string;
  code: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
}

interface Agency {
  name: string;
  code: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
  accounting_objective: number;
  negotiators: Negotiator[];
}

interface Director {
  id: string;
  name: string;
  code: string;
  company: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
  accounting_objective: number;
  agencies: Agency[];
}

export function useDirectionStats() {
  const { user } = useAuth();
  const [directors, setDirectors] = useState<Director[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = async (startDate: string, endDate: string, ranges: DateRange[] = [{ startDate, endDate }]) => {
    try {
      setLoading(true);
      setError('');

      // Récupérer l'année fiscale à partir de la date de début
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const fiscalYear = startDateObj.getMonth() + 1 < 4 
        ? startDateObj.getFullYear() - 1 
        : startDateObj.getFullYear();

      // 1. Charger les directeurs avec leurs agences
      const { data: directorsData, error: directorsError } = await supabase
        .from('negotiators')
        .select(`
          id,
          firstname,
          lastname,
          has_custom_objectives,
          monthly_objectives,
          negotiator_annual_objectives!left (
            fiscal_year,
            annual_objective
          ),
          negotiator_agencies!inner (
            agency,
            agencies!inner (
              name,
              company,
              has_custom_objectives,
              monthly_objectives,
              agency_annual_objectives!left (
                fiscal_year,
                annual_objective,
                accounting_objective
              )
            )
          )
        `)
        .eq('role', 'directeur')
        .neq('firstname', 'DIRECTION');

      if (directorsError) throw directorsError;

      // Charger les IDs EKYO (firstname = 'DIRECTION') pour les traiter séparément
      const { data: ekyoRaw } = await supabase
        .from('negotiators')
        .select('id')
        .eq('firstname', 'DIRECTION');
      const ekyoIdSet = new Set<string>((ekyoRaw || []).map((n: any) => n.id as string));

      // 2. Fonction pour calculer l'objectif pour une période donnée
      const calculatePeriodObjective = (entity: any, isAgency: boolean = false) => {
        let periodObjective = 0;

        // Si objectifs mensuels personnalisés
        if (entity.has_custom_objectives && entity.monthly_objectives?.[fiscalYear]) {
          const monthlyObjectives = entity.monthly_objectives[fiscalYear];
          
          // Calculer les mois inclus dans la période
          const startMonth = startDateObj.getMonth() + 1;
          const startYear = startDateObj.getFullYear();
          const endMonth = endDateObj.getMonth() + 1;
          const endYear = endDateObj.getFullYear();
          
          // Parcourir tous les mois de la période
          let currentDate = new Date(startYear, startMonth - 1, 1);
          const endDate = new Date(endYear, endMonth - 1, 1);
          
          while (currentDate <= endDate) {
            const monthKey = String(currentDate.getMonth() + 1).padStart(2, '0');
            if (isDateInRanges(`${currentDate.getFullYear()}-${monthKey}-01`, ranges)) {
              periodObjective += monthlyObjectives[monthKey] || 0;
            }
            currentDate.setMonth(currentDate.getMonth() + 1);
          }
        } else {
          // Sinon utiliser l'objectif annuel standard et le proratiser
          let fullAnnualObjective = 0;
          
          if (isAgency) {
            const annualObjective = entity.agency_annual_objectives?.find(
              obj => obj.fiscal_year === fiscalYear
            );
            fullAnnualObjective = annualObjective?.annual_objective || 0;
          } else {
            const annualObjective = entity.negotiator_annual_objectives?.find(
              obj => obj.fiscal_year === fiscalYear
            );
            fullAnnualObjective = annualObjective?.annual_objective || 0;
          }
          
          // Calculer le nombre de mois dans la période
          const monthsDiff = countRangeMonths(ranges);
          
          // Proratiser l'objectif annuel
          periodObjective = (fullAnnualObjective / 12) * monthsDiff;
        }

        return periodObjective;
      };

      // 3. Pour chaque directeur, charger les transactions et négociateurs
      const directorStats = await Promise.all((directorsData || []).map(async (director) => {
        const directorAgencies = director.negotiator_agencies.map(na => na.agency);

        // 2.1 Charger les négociateurs par agence
        const { data: negotiatorsData, error: negotiatorsError } = await supabase
          .from('negotiators')
          .select(`
            id,
            firstname,
            lastname,
            arrival_date,
            departure_date,
            has_custom_objectives,
            monthly_objectives,
            negotiator_annual_objectives!left (
              fiscal_year,
              annual_objective
            ),
            negotiator_agencies!inner (
              agency,
              objective_percentage,
              start_date,
              end_date
            )
          `)
          .in('negotiator_agencies.agency', directorAgencies)
          .neq('role', 'directeur')
          .neq('firstname', 'DIRECTION')
          .eq('archived', false);

        if (negotiatorsError) throw negotiatorsError;

        // Filtrer les négociateurs selon leurs dates d'arrivée et de départ
        const filteredNegotiatorsData = negotiatorsData.filter(negotiator => {
          const startDateObj = new Date(startDate);
          const endDateObj = new Date(endDate);
          
          // Si le négociateur a une date d'arrivée, vérifier qu'elle est avant ou pendant la période
          if (negotiator.arrival_date) {
            const arrivalDate = new Date(negotiator.arrival_date);
            if (arrivalDate > endDateObj) {
              return false; // Le négociateur n'était pas encore arrivé pendant cette période
            }
          }
          
          // Si le négociateur a une date de départ, vérifier qu'elle est après ou pendant la période
          if (negotiator.departure_date) {
            const departureDate = new Date(negotiator.departure_date);
            if (departureDate < startDateObj) {
              return false; // Le négociateur était déjà parti avant cette période
            }
          }
          
          return true;
        });

        // 2.2 Charger les transactions
        const { data: transactionsData, error: transactionsError } = await supabase
          .from('transactions')
          .select(`
            id,
            fees_without_tax,
            status,
            compromise_date,
            transaction_agencies!inner (
              agency
            ),
            transaction_retrocessions (
              amount,
              distribution_type,
              primary_negotiator_id,
              secondary_negotiator_id,
              tertiary_negotiator_id,
              quaternary_negotiator_id,
              distribution_percentages
            ),
            transaction_negotiators (
              negotiator_id,
              role,
              percentage,
              agency
            )
          `)
          .in('transaction_agencies.agency', directorAgencies)
          .or(`and(status.eq.potentiel),and(status.in.(compromis,acte,encaisse,cloture),or(${dateRangesFilter('compromise_date', ranges)}))`);

        if (transactionsError) throw transactionsError;

        // 3. Calculer les statistiques par agence
        const agencyStats = new Map<string, {
          total_sales: number;
          potential_sales: number;
          sales_count: number;
          negotiators: Map<string, {
            total_sales: number;
            potential_sales: number;
            sales_count: number;
          }>;
        }>();

        // Initialiser les statistiques pour chaque agence
        directorAgencies.forEach(agency => {
          agencyStats.set(agency, {
            total_sales: 0,
            potential_sales: 0,
            sales_count: 0,
            negotiators: new Map()
          });
        });

        // Résolution d'agence par date : tn.agency en priorité,
        // sinon l'association active à la date de la transaction
        const resolveAgency = (negotiatorId: string, txDate?: Date): string | undefined => {
          const n = filteredNegotiatorsData.find(neg => neg.id === negotiatorId);
          if (!n) return undefined;
          if (txDate) {
            const active = n.negotiator_agencies.find(na => {
              if (!directorAgencies.includes(na.agency)) return false;
              const s = na.start_date ? new Date(na.start_date) : null;
              const e = na.end_date   ? new Date(na.end_date)   : null;
              return (!s || s <= txDate) && (!e || e >= txDate);
            });
            if (active) return active.agency;
          }
          // Fallback : première association dans le périmètre
          return n.negotiator_agencies.find(na => directorAgencies.includes(na.agency))?.agency;
        };

        // Calculer les statistiques des transactions
        transactionsData?.forEach(transaction => {
          // Mapping des rétrocessions par négociateur (selon distribution_type)
          // Part de rétrocession par ligne : un négociateur présent dans deux agences
          // ne doit pas se voir déduire la rétrocession une fois par agence
          const retrocessionsPerRow = buildRetrocessionRowAmounts(
            transaction.transaction_retrocessions,
            transaction.transaction_negotiators
          );
          const txDate = transaction.compromise_date ? new Date(transaction.compromise_date) : undefined;

          transaction.transaction_negotiators.forEach((tn, rowIndex) => {
            if (ekyoIdSet.has(tn.negotiator_id)) return; // Part Direction — traitée séparément
            const negotiatorAgency = tn.agency || resolveAgency(tn.negotiator_id, txDate);
            const agencyData = negotiatorAgency ? agencyStats.get(negotiatorAgency) : undefined;

            if (agencyData) {
              const baseAmount = (transaction.fees_without_tax * tn.percentage) / 100;
              const retrocessionAmount = retrocessionsPerRow[rowIndex] || 0;
              const negotiatorAmount = baseAmount - retrocessionAmount;

              // Seuls les rôles "vendeur" et "vendeur/apporteur" comptent comme une vente
              // (Nb Transactions). Les autres rôles participent aux honoraires mais
              // ne sont pas comptabilisés comme une transaction vendue.
              const isSellingRole = tn.role === 'vendeur' || tn.role === 'vendeur_apporteur';

              if (transaction.status === 'potentiel') {
                agencyData.potential_sales += negotiatorAmount;
              } else {
                agencyData.total_sales += negotiatorAmount;
                if (isSellingRole) {
                  agencyData.sales_count++;
                }
              }

              // Statistiques par négociateur
              const negotiatorStats = agencyData.negotiators.get(tn.negotiator_id) || {
                total_sales: 0,
                potential_sales: 0,
                sales_count: 0
              };

              if (transaction.status === 'potentiel') {
                negotiatorStats.potential_sales += negotiatorAmount;
              } else {
                negotiatorStats.total_sales += negotiatorAmount;
                if (isSellingRole) {
                  negotiatorStats.sales_count++;
                }
              }

              agencyData.negotiators.set(tn.negotiator_id, negotiatorStats);
            }
          });
        });

        // 4. Formater les données pour chaque agence
        const formattedAgencies = director.negotiator_agencies.map(na => {
          const agency = na.agencies;
          const stats = agencyStats.get(agency.name) || {
            total_sales: 0,
            potential_sales: 0,
            sales_count: 0,
            negotiators: new Map()
          };

          // Récupérer les négociateurs de l'agence (actifs pendant la période selon les dates d'association)
          const periodStart = new Date(startDate);
          const periodEnd   = new Date(endDate);

          const agencyNegotiators = filteredNegotiatorsData
            .filter(n => {
              const assoc = n.negotiator_agencies.find(nag => nag.agency === agency.name);
              if (!assoc) return false;
              const s = assoc.start_date ? new Date(assoc.start_date) : null;
              const e = assoc.end_date   ? new Date(assoc.end_date)   : null;
              return (!s || s <= periodEnd) && (!e || e >= periodStart);
            })
            .map(n => {
              const stats = agencyStats.get(agency.name)?.negotiators.get(n.id) || {
                total_sales: 0,
                potential_sales: 0,
                sales_count: 0
              };

              // Calculer l'objectif du négociateur pour la période
              const fullNegotiatorObjective = calculatePeriodObjective(n, false);

              // Récupérer le pourcentage d'objectif pour cette agence
              const agencyAssociation = n.negotiator_agencies.find(na => na.agency === agency.name);
              const objectivePercentage = agencyAssociation?.objective_percentage ?? 100;
              
              // Appliquer le pourcentage pour cette agence
              const agencyNegotiatorObjective = (fullNegotiatorObjective * objectivePercentage) / 100;

              return {
                id: n.id,
                name: `${n.firstname} ${n.lastname}`,
                code: `NEGO-${n.id.slice(0, 4)}`,
                total_sales: stats.total_sales,
                potential_sales: stats.potential_sales,
                sales_count: stats.sales_count,
                annual_objective: agencyNegotiatorObjective,
                objective_percentage: objectivePercentage
              };
            });

          // Ajouter le directeur s'il a des transactions dans cette agence
          const directorInAgency = agencyStats.get(agency.name)?.negotiators.get(director.id);
          if (directorInAgency && (directorInAgency.total_sales > 0 || directorInAgency.potential_sales > 0)) {
            const directorPeriodObjective = calculatePeriodObjective(director, false);
            agencyNegotiators.push({
              id: director.id,
              name: `${director.firstname} ${director.lastname}`,
              code: `NEGO-${director.id.slice(0, 4)}`,
              total_sales: directorInAgency.total_sales,
              potential_sales: directorInAgency.potential_sales,
              sales_count: directorInAgency.sales_count,
              annual_objective: directorPeriodObjective,
              objective_percentage: 100
            });
          }

          // Calculer l'objectif de l'agence pour la période
          const agencyObjective = calculatePeriodObjective(agency, true);
          
          // Calculer la somme des objectifs pondérés des négociateurs
          const negotiatorsObjectiveSum = agencyNegotiators.reduce((sum, neg) => {
            return sum + (neg.annual_objective || 0);
          }, 0);
          
          // Utiliser l'objectif de l'agence s'il existe, sinon utiliser la somme des objectifs des négociateurs
          const finalAgencyObjective = agencyObjective > 0 ? agencyObjective : negotiatorsObjectiveSum;
          
          // Pour l'objectif comptable, on le proratise aussi
          let accountingObjective = 0;
          if (agency.has_custom_objectives && agency.monthly_objectives?.[fiscalYear]) {
            // Si objectifs personnalisés, on utilise la même logique
            accountingObjective = finalAgencyObjective;
          } else {
            const annualObjective = agency.agency_annual_objectives?.find(
              obj => obj.fiscal_year === fiscalYear
            );
            const fullAccountingObjective = annualObjective?.accounting_objective || 0;
            
            // Calculer le nombre de mois dans la période
            const monthsDiff = countRangeMonths(ranges);
            
            // Proratiser l'objectif comptable
            accountingObjective = (fullAccountingObjective / 12) * monthsDiff;
          }

          return {
            name: agency.name,
            code: agency.name,
            total_sales: stats.total_sales,
            potential_sales: stats.potential_sales,
            sales_count: stats.sales_count,
            annual_objective: finalAgencyObjective,
            accounting_objective: accountingObjective,
            negotiators: agencyNegotiators
          };
        });

        // 5. Calculer les totaux du directeur
        const directorTotals = formattedAgencies.reduce(
          (acc, agency) => ({
            total_sales: acc.total_sales + agency.total_sales,
            potential_sales: acc.potential_sales + agency.potential_sales,
            sales_count: acc.sales_count + agency.sales_count,
            annual_objective: acc.annual_objective + agency.annual_objective,
            accounting_objective: acc.accounting_objective + agency.accounting_objective
          }),
          { total_sales: 0, potential_sales: 0, sales_count: 0, annual_objective: 0, accounting_objective: 0 }
        );

        return {
          id: director.id,
          name: `${director.firstname} ${director.lastname}`,
          code: `DIR-${director.firstname.slice(0, 3)}`,
          company: director.negotiator_agencies[0].agencies.company,
          ...directorTotals,
          agencies: formattedAgencies
        };
      }));

      // 6. Trier les directeurs par CA réalisé
      const sortedDirectors = directorStats.sort((a, b) => b.total_sales - a.total_sales);

      // 7. Part Direction (EKYO) — agrégée par company, tous rôles confondus
      if (ekyoIdSet.size > 0) {
        // Mapping agence → company
        const { data: allAgenciesRaw } = await supabase
          .from('agencies')
          .select('name, company');
        const agencyToCompany = new Map<string, string>(
          (allAgenciesRaw || []).map((a: any) => [a.name as string, a.company as string])
        );

        // Lignes transaction_negotiators d'EKYO
        const { data: ekyoTnRows } = await supabase
          .from('transaction_negotiators')
          .select('transaction_id, negotiator_id, percentage, agency')
          .in('negotiator_id', [...ekyoIdSet]);

        if (ekyoTnRows && ekyoTnRows.length > 0) {
          const ekyoTxIds = [...new Set(ekyoTnRows.map(r => r.transaction_id))];

          // Transactions correspondantes filtrées par période
          const { data: ekyoTxRows } = await supabase
            .from('transactions')
            .select(`
              id,
              fees_without_tax,
              status,
              compromise_date,
              transaction_retrocessions(
                amount,
                distribution_type,
                primary_negotiator_id,
                secondary_negotiator_id,
                tertiary_negotiator_id,
                quaternary_negotiator_id,
                distribution_percentages
              ),
              transaction_agencies(agency)
            `)
            .in('id', ekyoTxIds)
            .or(`status.eq.potentiel,and(status.in.(compromis,acte,encaisse,cloture),or(${dateRangesFilter('compromise_date', ranges)}))`);

          const txMap = new Map((ekyoTxRows || []).map(tx => [tx.id, tx]));

          interface EkyoAgencyStats { total: number; potential: number; txIds: Set<string> }
          interface EkyoCompanyStats {
            total: number; potential: number; txIds: Set<string>;
            byAgency: Map<string, EkyoAgencyStats>;
          }
          const byCompany = new Map<string, EkyoCompanyStats>();

          // Rétrocessions calculées par transaction, sur l'ensemble des lignes EKYO
          // de celle-ci (une même personne peut y figurer pour deux agences)
          const ekyoRowsByTx = new Map<string, typeof ekyoTnRows>();
          ekyoTnRows.forEach(tn => {
            const list = ekyoRowsByTx.get(tn.transaction_id) || [];
            list.push(tn);
            ekyoRowsByTx.set(tn.transaction_id, list);
          });
          const ekyoRetrocessionByRow = new Map<typeof ekyoTnRows[number], number>();
          ekyoRowsByTx.forEach((txRows, transactionId) => {
            const tx = txMap.get(transactionId);
            if (!tx) return;
            const amounts = buildRetrocessionRowAmounts((tx as any).transaction_retrocessions, txRows);
            txRows.forEach((tn, i) => ekyoRetrocessionByRow.set(tn, amounts[i]));
          });

          ekyoTnRows.forEach(tn => {
            const tx = txMap.get(tn.transaction_id);
            if (!tx) return; // hors période

            const baseAmount = (tx.fees_without_tax * tn.percentage) / 100;
            const retrocessionAmount = ekyoRetrocessionByRow.get(tn) || 0;
            const amount = baseAmount - retrocessionAmount;

            // Déterminer l'agence et la company
            const agencyName: string = tn.agency || ((tx as any).transaction_agencies || [])[0]?.agency || '';
            let company: string | undefined;
            if (agencyName) {
              company = agencyToCompany.get(agencyName);
            }
            if (!company) return;

            const s = byCompany.get(company) ?? {
              total: 0, potential: 0, txIds: new Set<string>(),
              byAgency: new Map<string, EkyoAgencyStats>(),
            };
            const ag = s.byAgency.get(agencyName) ?? { total: 0, potential: 0, txIds: new Set<string>() };

            if (tx.status === 'potentiel') {
              s.potential += amount;
              ag.potential += amount;
            } else {
              s.total += amount;
              s.txIds.add(tx.id);
              ag.total += amount;
              ag.txIds.add(tx.id);
            }
            s.byAgency.set(agencyName, ag);
            byCompany.set(company, s);
          });

          // Injecter une ligne Direction synthétique par company, avec détail par agence
          byCompany.forEach((stats, company) => {
            const agencies = [...stats.byAgency.entries()].map(([agencyName, ag]) => ({
              name: agencyName,
              code: agencyName,
              total_sales: ag.total,
              potential_sales: ag.potential,
              sales_count: ag.txIds.size,
              annual_objective: 0,
              accounting_objective: 0,
              negotiators: [],
            }));

            sortedDirectors.push({
              id: `ekyo-direction-${company}`,
              name: 'Direction',
              code: 'DIR-EKYO',
              company,
              total_sales: stats.total,
              potential_sales: stats.potential,
              sales_count: stats.txIds.size,
              annual_objective: 0,
              accounting_objective: 0,
              agencies,
            });
          });
        }
      }

      setDirectors(sortedDirectors);
    } catch (err: any) {
      console.error('Error loading direction stats:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  return { directors, loading, error, loadData };
}